import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MoreHorizontal, Plus, RefreshCw } from "lucide-react";
import { formatDateTime, formatMoney } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { channelName, fmtInt, fmtMoneyWhole, fmtNum1 } from "../ads";
import {
  biddingLabel, budgetRange, changeStatus, formatCustomerId, servingNote, statusLabel,
  type CampaignsResponse, type LiveCampaign, type ProposeResult,
} from "../campaigns";
import ConnectionNotice from "../components/ConnectionNotice";
import Dialog from "../components/Dialog";
import QuickMenu, { QuickMenuItem } from "../components/QuickMenu";
import CampaignBuilder from "../components/ads/CampaignBuilder";
import { EmptyState, ErrorState, PageHeader, PageSkeleton, StatusBadge } from "../components/Ui";
import { NoClient, useMutation } from "../components/Workflow";

/** "JK|Sacramento|PMax|Leads|V1" may break after each "|" on a phone, never inside a word. */
const breakAtPipes = (name: string) => name.split("|").flatMap((part, i, all) => (i < all.length - 1 ? [part + "|", <wbr key={i} />] : [part]));
// Google names LSA campaigns "LocalServicesCampaign:SystemGenerated:…"; the account's name says which one it is.
const campaignName = (c: LiveCampaign, account?: string) => (/^LocalServicesCampaign:/.test(c.name) ? `Local Services${account ? `, ${account}` : ""}` : c.name);

/** Run a client's Google Ads from NL-AOS: live campaigns, on/off, budgets, blocked searches, and new campaigns. */
export default function AdsCampaigns() {
  const { clientId } = useClientSelection();
  const [params] = useSearchParams();
  const [notice, setNotice] = useState("");
  if (!clientId) return <><PageHeader title="Campaigns" description="Pick a client in the sidebar." /><NoClient /></>;
  if (params.get("view") === "new") return <CampaignBuilder key={clientId} clientId={clientId} account={params.get("account") || ""} onDone={setNotice} />;
  return <CampaignList key={clientId} clientId={clientId} notice={notice} clearNotice={() => setNotice("")} />;
}

type Pending =
  | { kind: "status"; campaign: LiveCampaign; status: "ENABLED" | "PAUSED" }
  | { kind: "budget"; campaign: LiveCampaign }
  | { kind: "negatives"; campaign: LiveCampaign };

function CampaignList({ clientId, notice, clearNotice }: { clientId: number; notice: string; clearNotice: () => void }) {
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const { client } = useClientSelection();
  const [params, setParams] = useSearchParams();
  const account = params.get("account") || "";
  const { data, loading, refreshing, error, reload } = useApi<CampaignsResponse>(`/api/ads-manager/${clientId}/campaigns${account ? `?account=${account}` : ""}`);
  // The same request ConnectionNotice makes (cached a minute): when its box already says Google
  // stopped, the live read's own "not connected" error would say it a second time.
  const feeds = useApi<Record<string, { status?: string }>>(`/api/connections/health?client_id=${clientId}`);
  const googleDown = ["needs_key", "failing", "stale"].includes(feeds.data?.google_ads?.status || "");
  const m = useMutation();
  const [pending, setPending] = useState<Pending | null>(null);
  const [undoing, setUndoing] = useState<{ id: number; title: string } | null>(null);
  if (loading && !data) return <><PageHeader title="Campaigns" description="Reading Google Ads…" /><PageSkeleton cards={2} /></>;
  if (error || !data) return <><PageHeader title="Campaigns" /><ErrorState message={error || "Campaigns could not load."} onRetry={reload} /></>;

  const current = data.account?.customer_id || account || data.accounts[0]?.customer_id || "";
  const newLink = `/ads-campaigns?client_id=${clientId}&view=new${current ? `&account=${current}` : ""}`;
  const pickAccount = (id: string) => { const next = new URLSearchParams(params); next.set("account", id); setParams(next, { replace: true }); };
  const act = async (path: string, body: Record<string, unknown>) => {
    clearNotice();
    const r = await m.run<ProposeResult>(path, body);
    if (!r) return false;
    m.setNotice(r.message);
    reload();
    return true;
  };
  const undo = async (approvalId: number) => {
    clearNotice();
    const r = await m.run<{ message: string }>(`/api/approvals/${approvalId}/rollback`, {});
    if (!r) return false;
    m.setNotice(r.message);
    reload();
    return true;
  };

  const header = (
    <PageHeader
      title="Campaigns"
      description={data.account ? `Live from Google Ads: ${data.account.name}, ${formatCustomerId(data.account.customer_id)}. Last 30 days.` : `${client?.name || "This client"}'s Google Ads campaigns.`}
      actions={<>
        {data.accounts.length > 1 && (
          <select className="campaigns-account" aria-label="Google Ads account" value={current} onChange={(e) => pickAccount(e.target.value)}>
            {data.accounts.map((a) => <option key={a.customer_id} value={a.customer_id}>{a.name}</option>)}
          </select>
        )}
        {/* No account linked: the empty state and its link only, as on the Ads report. */}
        {data.accounts.length > 0 && <button className="button secondary" disabled={refreshing} onClick={reload}><RefreshCw className={refreshing ? "spin" : ""} aria-hidden="true" />{refreshing ? "Refreshing…" : "Refresh"}</button>}
        {data.accounts.length > 0 && <Link className="button" to={newLink}><Plus aria-hidden="true" />New campaign</Link>}
      </>}
    />
  );

  if (!data.accounts.length) {
    return (
      <>
        {header}
        <EmptyState
          title="No Google Ads account linked"
          description={admin ? "Link this client's Google Ads account to run its campaigns from here." : "An administrator links accounts in Setup → Connections."}
          action={admin ? <Link className="button secondary" to={`/client-settings?client_id=${clientId}`}>Open Connections</Link> : undefined}
        />
      </>
    );
  }

  const confirmLabel = admin ? "Apply now" : "Send for approval";
  return (
    <>
      {header}
      <ConnectionNotice clientId={clientId} feeds={["google_ads"]} />
      {notice && <p className="inline-notice success" role="status"><span>{notice}</span><button type="button" aria-label="Dismiss" onClick={clearNotice}>×</button></p>}
      {!pending && !undoing && m.feedback}
      {data.error && !googleDown && <p className="inline-notice warning" role="status">{data.error}</p>}
      {data.stats_error && <p className="inline-notice warning" role="status">{data.stats_error}</p>}

      {!data.error && (data.campaigns.length ? (
        <section className="panel campaigns-panel">
          <div className="table-wrap">
            <table className="ads-table campaigns-table">
              <thead><tr>
                <th>Campaign</th><th>Status</th><th className="campaigns-col-extra">Type</th><th className="num">Daily budget</th>
                <th className="campaigns-col-extra">Bidding</th><th className="num">Spend</th><th className="num campaigns-col-extra">Clicks</th>
                <th className="num">Conv.</th><th className="num">CPA</th><th><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>{data.campaigns.map((c) => {
                const note = servingNote(c);
                return (
                  <tr key={c.id}>
                    <td className="campaigns-name-cell"><strong className="campaigns-name" title={c.name}>{breakAtPipes(campaignName(c, data.account?.name))}</strong></td>
                    <td data-label="Status">
                      {c.editable ? (
                        <QuickMenu label={`Status of ${c.name}`} trigger={<span className={`campaign-status ${c.status === "ENABLED" ? "on" : ""}`}>{statusLabel(c.status)}</span>}>
                          {(close) => (<>
                            <QuickMenuItem active={c.status === "ENABLED"} onClick={() => { close(); if (c.status !== "ENABLED") setPending({ kind: "status", campaign: c, status: "ENABLED" }); }}>On</QuickMenuItem>
                            <QuickMenuItem active={c.status === "PAUSED"} onClick={() => { close(); if (c.status !== "PAUSED") setPending({ kind: "status", campaign: c, status: "PAUSED" }); }}>Paused</QuickMenuItem>
                          </>)}
                        </QuickMenu>
                      ) : <span className={`campaign-status ${c.status === "ENABLED" ? "on" : ""}`}>{statusLabel(c.status)}</span>}
                      {(note || !c.editable) && <small className="campaigns-serving">{[note, !c.editable ? "Run by Google" : null].filter(Boolean).join(", ")}</small>}
                    </td>
                    <td className="campaigns-col-extra campaigns-nowrap" data-label="Type">{channelName(c.channel)}</td>
                    <td className="num" data-label="Daily budget">
                      {c.daily_budget === null ? "—" : c.editable && !c.shared_budget
                        ? <button type="button" className="text-button campaigns-budget" onClick={() => setPending({ kind: "budget", campaign: c })}>{formatMoney(c.daily_budget)}</button>
                        : <>{formatMoney(c.daily_budget)}{c.shared_budget && <small className="campaigns-serving">Shared</small>}</>}
                    </td>
                    <td className="campaigns-col-extra campaigns-nowrap" data-label="Bidding">{biddingLabel(c.bidding)}{targetOf(c) && <small className="campaigns-serving">{targetLabel(targetOf(c)!)}</small>}</td>
                    <td className="num" data-label="Spend">{fmtMoneyWhole(c.spend)}</td>
                    <td className="num campaigns-col-extra" data-label="Clicks">{fmtInt(c.clicks)}</td>
                    <td className="num" data-label="Conv.">{fmtNum1(c.conversions)}</td>
                    <td className="num" data-label="CPA">{formatMoney(c.cpa)}</td>
                    <td className="campaigns-actions">
                      {c.editable ? (
                        <QuickMenu align="right" label={`Actions for ${c.name}`} trigger={<MoreHorizontal aria-hidden="true" />}>
                          {(close) => (<>
                            <QuickMenuItem onClick={() => { close(); setPending({ kind: "status", campaign: c, status: c.status === "ENABLED" ? "PAUSED" : "ENABLED" }); }}>{c.status === "ENABLED" ? "Pause campaign" : "Turn on campaign"}</QuickMenuItem>
                            {!c.shared_budget && c.daily_budget !== null && <QuickMenuItem onClick={() => { close(); setPending({ kind: "budget", campaign: c }); }}>Change daily budget</QuickMenuItem>}
                            <QuickMenuItem onClick={() => { close(); setPending({ kind: "negatives", campaign: c }); }}>Block searches</QuickMenuItem>
                          </>)}
                        </QuickMenu>
                      ) : null}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </section>
      ) : (
        <EmptyState title="No campaigns in this account yet" description="Create the first one here. It starts paused, so nothing spends until you turn it on." action={<Link className="button" to={newLink}><Plus aria-hidden="true" />New campaign</Link>} />
      ))}

      <Changes data={data} admin={admin} busy={m.busy} onUndo={(id, title) => setUndoing({ id, title })} />
      {undoing && (
        <Dialog title="Undo this change?" description={undoing.title} onClose={() => setUndoing(null)}>
          <div className="operation-form">
            <p className="campaigns-dialog-copy">{/^Create/.test(undoing.title) ? "The campaign is removed from Google Ads. This only works while it is still paused and has never shown an ad." : "Google Ads goes back to how it was before this change."}</p>
            {m.error && <p className="inline-notice danger" role="alert">{m.error}</p>}
            <div className="operation-actions">
              <button type="button" className="button secondary" onClick={() => setUndoing(null)}>Keep it</button>
              <button type="button" className="button danger" disabled={m.busy} onClick={async () => { if (await undo(undoing.id)) setUndoing(null); }}>{m.busy ? "Undoing…" : "Undo now"}</button>
            </div>
          </div>
        </Dialog>
      )}

      {pending?.kind === "status" && (
        <ConfirmStatus pending={pending} admin={admin} busy={m.busy} error={m.error} onCancel={() => setPending(null)}
          onConfirm={async (reason) => { if (await act(`/api/ads-manager/${clientId}/campaigns/${pending.campaign.id}/status`, { account: current, status: pending.status, reason, apply_now: admin })) setPending(null); }} />
      )}
      {pending?.kind === "budget" && (
        <BudgetDialog campaign={pending.campaign} label={confirmLabel} admin={admin} busy={m.busy} error={m.error} onCancel={() => setPending(null)}
          onSave={async (daily, reason, targetReviewed) => { if (await act(`/api/ads-manager/${clientId}/campaigns/${pending.campaign.id}/budget`, { account: current, daily_budget: daily, reason, target_reviewed: targetReviewed, apply_now: admin })) setPending(null); }} />
      )}
      {pending?.kind === "negatives" && (
        <NegativesDialog campaign={pending.campaign} label={confirmLabel} busy={m.busy} error={m.error} onCancel={() => setPending(null)}
          onSave={async (terms, match, reason) => { if (await act(`/api/ads-manager/${clientId}/campaigns/${pending.campaign.id}/negatives`, { account: current, terms, match_type: match, reason, apply_now: admin })) setPending(null); }} />
      )}
    </>
  );
}

function Changes({ data, admin, busy, onUndo }: { data: CampaignsResponse; admin: boolean; busy: boolean; onUndo: (approvalId: number, title: string) => void }) {
  if (!data.changes.length && !data.waiting.length) return null;
  return (
    <section className="panel campaigns-changes">
      <div className="panel-heading"><div><h2>Changes made in NOLA</h2></div></div>
      <ul className="campaigns-change-list">
        {data.waiting.map((w) => (
          <li key={`w${w.approval_id}`}>
            {/* The same head as the rows below: a Waiting badge, then the change. */}
            <div>
              <span className="campaigns-change-head"><StatusBadge tone="warning">Waiting</StatusBadge><strong>{w.title}</strong></span>
              <p className="muted">{[w.submitted_by_name ? `Sent by ${w.submitted_by_name}` : null, formatDateTime(w.submitted_at)].filter(Boolean).join(", ")}</p>
            </div>
            <Link className="text-button" to="/approvals">Open Approvals</Link>
          </li>
        ))}
        {data.changes.map((c) => {
          const s = changeStatus(c.status);
          const when = c.status === "undone" ? c.undone_at : c.applied_at || c.created_at;
          const who = c.status === "undone" ? c.undone_by_name : c.applied_by_name;
          return (
            <li key={c.id}>
              <div>
                <span className="campaigns-change-head"><StatusBadge tone={s.tone}>{s.label}</StatusBadge><strong>{c.title}</strong></span>
                <p className="muted">{[who, when ? formatDateTime(when) : null].filter(Boolean).join(", ")}</p>
                {c.error && <p className="campaigns-change-error">{c.error}</p>}
              </div>
              {admin && c.status === "applied" && c.undoable && c.approval_id && <button type="button" className="text-button" disabled={busy} onClick={() => onUndo(c.approval_id!, c.title)}>Undo</button>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ConfirmStatus({ pending, admin, busy, error, onCancel, onConfirm }: { pending: Extract<Pending, { kind: "status" }>; admin: boolean; busy: boolean; error: string; onCancel: () => void; onConfirm: (reason: string) => void }) {
  const on = pending.status === "ENABLED";
  const c = pending.campaign;
  const [reason, setReason] = useState("");
  const brand = !on && /\bbrand(ed)?\b/i.test(c.name);
  function submit(e: FormEvent) { e.preventDefault(); if (reasonOk(reason)) onConfirm(reason.trim()); }
  return (
    <Dialog title={on ? `Turn on "${pipeBreaks(c.name)}"?` : `Pause "${pipeBreaks(c.name)}"?`} onClose={onCancel}>
      <form className="operation-form" onSubmit={submit}>
        <p className="campaigns-dialog-copy">
          {on ? `Ads start showing and the campaign can spend up to ${c.daily_budget !== null ? `${formatMoney(c.daily_budget)} a day` : "its budget"}.`
            : "Its ads stop showing until it is turned on again. Nothing else changes."}
        </p>
        {brand && <p className="inline-notice warning" role="status">The playbook keeps brand campaigns on, even in a budget crunch.</p>}
        {on && c.daily_budget !== null && c.daily_budget < MIN_DAILY && <p className="inline-notice warning" role="status">Its budget is {formatMoney(c.daily_budget)} a day. The playbook says fund a campaign to {formatMoney(MIN_DAILY)} a day or keep it paused.</p>}
        <ReasonField value={reason} onChange={setReason} placeholder={on ? "Client approved the spend" : "Client is booked out for two weeks"} />
        {!admin && <p className="campaigns-hint">A superadmin approves it in Approvals, and it is applied then.</p>}
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="button" disabled={busy || !reasonOk(reason)}>{busy ? "Working…" : !admin ? "Send for approval" : on ? "Turn on" : "Pause"}</button>
        </div>
      </form>
    </Dialog>
  );
}

/** The campaign's own bid target (the server reads it with the campaign). */
type Target = { kind: "cpa" | "roas"; value: number };
const targetOf = (c: LiveCampaign) => (c as LiveCampaign & { target?: Target | null }).target || null;
const targetLabel = (t: Target) => (t.kind === "cpa" ? `Target ${formatMoney(t.value)} per conversion` : `Target ${Math.round(t.value * 100)}% ROAS`);

/** "JK|Sacramento|Brand" in a dialog title may break after a "|", never inside a word. */
const pipeBreaks = (name: string) => name.replace(/\|/g, "|\u200b");

/** KB-004 §7: every change is logged with its reason. */
const reasonOk = (s: string) => s.trim().length >= 3;
function ReasonField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label>Why
      <input value={value} onChange={(e) => onChange(e.target.value)} maxLength={300} placeholder={placeholder} />
    </label>
  );
}

/**
 * The budgets the playbook allows from here (the server checks the same): 20% either way
 * (KB-019), and never under $15 a day while the campaign is on (KB-004).
 */
const MIN_DAILY = 15;
function budgetLimits(current: number, enabled: boolean) {
  const r = budgetRange(current);
  return { min: enabled ? Math.max(r.min, Math.min(MIN_DAILY, current)) : r.min, max: r.max };
}

function BudgetDialog({ campaign, label, admin, busy, error, onCancel, onSave }: { campaign: LiveCampaign; label: string; admin: boolean; busy: boolean; error: string; onCancel: () => void; onSave: (daily: number, reason: string, targetReviewed: boolean) => void }) {
  const now = campaign.daily_budget || 0;
  const enabled = campaign.status === "ENABLED";
  const range = budgetLimits(now, enabled);
  const target = targetOf(campaign);
  const [value, setValue] = useState(String(now));
  const [reason, setReason] = useState("");
  const [checked, setChecked] = useState(false);
  const n = Math.round(Number(value) * 100) / 100;
  const outside = !(n >= range.min && n <= range.max);
  // The agency reviews current targets before budget changes; a target edit requires its own justification.
  const needsCheck = !!target && admin && !checked;
  function submit(e: FormEvent) { e.preventDefault(); if (!outside && n !== now && reasonOk(reason) && !needsCheck) onSave(n, reason.trim(), checked); }
  return (
    <Dialog title="Change daily budget" description={campaign.name} onClose={onCancel}>
      <form className="operation-form" onSubmit={submit}>
        <label>Daily budget
          <span className="campaigns-money"><span aria-hidden="true">$</span><input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} autoFocus /></span>
        </label>
        <p className="campaigns-hint">
          Now {formatMoney(now)} a day, so {formatMoney(range.min)} to {formatMoney(range.max)}. Budgets move at most 20% at a time and rise once a week{enabled ? `, and stay at ${formatMoney(MIN_DAILY)} or more while the campaign is on` : ""}.
        </p>
        <ReasonField value={reason} onChange={setReason} placeholder="Lost 30% of impressions to budget" />
        {target && (admin ? (
          <label className="campaigns-check">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <span>{targetLabel(target)}. I checked qualified lead cost, conversion delay and recent changes. The existing target still fits {Number.isFinite(n) && n > 0 ? formatMoney(n) : "the new budget"} a day.</span>
          </label>
        ) : <p className="campaigns-hint">{targetLabel(target)}. The approver checks it still fits the new budget.</p>)}
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="button" disabled={busy || outside || n === now || !reasonOk(reason) || needsCheck}>{busy ? "Working…" : label}</button>
        </div>
      </form>
    </Dialog>
  );
}

function NegativesDialog({ campaign, label, busy, error, onCancel, onSave }: { campaign: LiveCampaign; label: string; busy: boolean; error: string; onCancel: () => void; onSave: (terms: string[], match: string, reason: string) => void }) {
  const [text, setText] = useState("");
  const [match, setMatch] = useState("PHRASE");
  const [reason, setReason] = useState("");
  const terms = text.split(/\n|,/).map((t) => t.trim()).filter(Boolean);
  function submit(e: FormEvent) { e.preventDefault(); if (terms.length && reasonOk(reason)) onSave(terms, match, reason.trim()); }
  return (
    <Dialog title="Block searches" description={`Ads in ${campaign.name} stop showing for these searches.`} onClose={onCancel}>
      <form className="operation-form" onSubmit={submit}>
        <label>Negative keywords, one per line
          <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={"jobs\ndiy\nhow to"} autoFocus />
        </label>
        <label>Match
          <select value={match} onChange={(e) => setMatch(e.target.value)}>
            <option value="PHRASE">Phrase: any search containing the words</option>
            <option value="EXACT">Exact: only that search</option>
            <option value="BROAD">Broad: searches with all the words, any order</option>
          </select>
        </label>
        <ReasonField value={reason} onChange={setReason} placeholder="Job seekers, no conversions" />
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="button" disabled={busy || !terms.length || !reasonOk(reason)}>{busy ? "Working…" : `${label}${terms.length > 1 ? ` (${terms.length})` : ""}`}</button>
        </div>
      </form>
    </Dialog>
  );
}
